import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { APIGestaCardService } from 'app/core/api/gestacard.service';
import { ExameUltrasom } from 'app/core/interfaces/pacient-info.interface';
import { UltrasonografiaConfiance } from 'app/core/interfaces/pacient-info.interface';
import { logger } from 'app/core/utils/console';

@Component({
  selector: 'app-modal-analisar-ultrasonografia',
  templateUrl: './modal-analisar-ultrasonografia.component.html',
  styleUrls: ['./modal-analisar-ultrasonografia.component.scss']
})
export class ModalAnalisarUltrasonografiaComponent implements OnInit {
  isLoading: boolean = false;
  ultrasonografiasAnalisadas: UltrasonografiaConfiance[] = [];
  ultrasonografiasList: ExameUltrasom[] = [];
  ultrasonografiasBackup: ExameUltrasom[] = [];
  errorMessage: string = '';
  arquivoId: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      formData?: FormData,
      cartaoId: string,
      ultrasonografias: ExameUltrasom[],
      preAnalisedUSG?: UltrasonografiaConfiance[],
      preAnalisedArquivoId?: string
    },
    public dialogRef: MatDialogRef<ModalAnalisarUltrasonografiaComponent>,
    private _apiGestaCard: APIGestaCardService,
    private _changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.ultrasonografiasList = JSON.parse(JSON.stringify(this.data.ultrasonografias || []));
    this.ultrasonografiasBackup = JSON.parse(JSON.stringify(this.data.ultrasonografias || []));
    if (this.data.preAnalisedUSG && this.data.preAnalisedUSG.length > 0) {
      this.arquivoId = this.data.preAnalisedArquivoId || '';
      this.ultrasonografiasAnalisadas = this.data.preAnalisedUSG.map(u => ({ ...u, used: false }));
      this.autoAplicarAltaConfianca();
    } else if (this.data.formData) {
      this.analisarUltrasonografias();
    } else {
      this.errorMessage = 'Não foi possível carregar os dados da ultrassonografia. Tente novamente.';
    }
  }

  abrirPDF(): void {
    if (!this.data.formData) return;
    const leitor = new FileReader();

    leitor.onload = (e: any) => {
      const pdfDataUrl = e.target.result;
      const pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write(`
          <html>
            <head>
              <title>Visualizador de PDF</title>
            </head>
            <body style="margin:0">
              <embed width="100%" height="100%" src="${pdfDataUrl}" type="application/pdf">
            </body>
          </html>
        `);
      } else {
        alert('Não foi possível abrir o PDF. Verifique se o bloqueador de pop-ups está desativado.');
      }
    };

    leitor.readAsDataURL(this.data.formData!.get('pdf') as File);
  }

  async analisarUltrasonografias(): Promise<void> {
    try {
      this.isLoading = true;
      this.errorMessage = '';

      const resultado = await this._apiGestaCard.AnalisarPDFUltrasonografia(
        this.data.cartaoId,
        this.data.formData!.get('pdf') as File
      );

      this.ultrasonografiasAnalisadas = resultado.ultrasonografias;
      this.arquivoId = resultado.arquivoId;

      if (this.ultrasonografiasAnalisadas.length > 0) {
        this.autoAplicarAltaConfianca();
      }

      logger.log('ultrasonografiasAnalisadas', this.ultrasonografiasAnalisadas)

    } catch (error) {
      this.errorMessage = 'Erro ao analisar o PDF de ultrassonografia. Tente novamente.';
    } finally {
      this.isLoading = false;
    }
  }

  private autoAplicarAltaConfianca(): void {
    this.ultrasonografiasAnalisadas.forEach(usgConfiance => {
      if (usgConfiance.confidence >= 90) {
        usgConfiance.used = true;
        this.adicionarUltrasonografia(usgConfiance, true);
      }
    });
  }

  private adicionarUltrasonografia(usgConfiance: UltrasonografiaConfiance, silent: boolean = false): void {
    const novaUsg: ExameUltrasom = {
      data: usgConfiance.data || new Date().toISOString().split('T')[0],
      ig: usgConfiance.ig || '',
      peso_fetal: usgConfiance.peso_fetal || '',
      placenta: usgConfiance.placenta || '',
      outros: usgConfiance.outros || '',
      arquivo_id: this.arquivoId || null
    };

    this.ultrasonografiasList.push(novaUsg);

    logger.log('ultrasonografiasList', this.ultrasonografiasList)

    if (!silent) {
      this._changeDetector.detectChanges();
    }
  }

  private removerUltrasonografia(usgConfiance: UltrasonografiaConfiance): void {
    const index = this.ultrasonografiasList.findIndex(u =>
      u.ig === (usgConfiance.ig || '') &&
      u.peso_fetal === (usgConfiance.peso_fetal || '') &&
      u.placenta === (usgConfiance.placenta || '') &&
      u.outros === (usgConfiance.outros || '')
    );

    if (index > -1) {
      this.ultrasonografiasList.splice(index, 1);
    }
  }

  usarUltrasonografia(usgConfiance: UltrasonografiaConfiance): void {
    usgConfiance.used = true;
    this.adicionarUltrasonografia(usgConfiance);
  }

  pararDeUsar(usgConfiance: UltrasonografiaConfiance): void {
    usgConfiance.used = false;
    this.removerUltrasonografia(usgConfiance);
    this._changeDetector.detectChanges();
  }

  getResumoUsg(usgConfiance: UltrasonografiaConfiance): string {
    const partes: string[] = [];
    if (usgConfiance.ig) partes.push(`IG: ${usgConfiance.ig}`);
    if (usgConfiance.peso_fetal) partes.push(`Peso: ${usgConfiance.peso_fetal}`);
    if (usgConfiance.placenta) partes.push(`Placenta: ${usgConfiance.placenta}`);
    if (usgConfiance.outros) partes.push(`Outros: ${usgConfiance.outros}`);
    return partes.join(' | ') || 'Sem dados extraídos';
  }

  descartarDados(): void {
    this.dialogRef.close({ action: 'discard' });
  }

  finalizarEdicao(): void {
    this.dialogRef.close({
      action: 'save',
      arquivoId: this.arquivoId,
      ultrasonografias: this.ultrasonografiasList
    });
  }
}
